import React, { useState } from 'react';
import { Box, Backdrop, Button, TextField, Typography, InputAdornment, IconButton, Paper, Container, Grow } from '@mui/material';
import { Email, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import CircularProgress from '@mui/material/CircularProgress';
import { toast } from 'react-toastify';
import styles from "./index.module.scss";

import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

// Import the new API system
import API from '../../api';

import { InputField } from 'JsUtils/FormikControls';
import CustomButton from 'Common/CustomButton';

const AdminSignIn = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [backdrop, setBackdrop] = useState(false);
    const [mfaRequired, setMfaRequired] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    // FORMIK INITIAL VALUES
    const initialValues = {
        username: '',
        password: '',
    }

    // MFA FORM INITIAL VALUES
    const mfaInitialValues = {
        otpCode: '',
    }

    // YUP VALIDATION SCHEMA
    const validationSchema = Yup.object({
        username: Yup.string().required('Username is required'),
        password: Yup.string().required('Password is required'),
    })

    // YUP VALIDATION SCHEMA FOR MFA
    const mfaValidationSchema = Yup.object({
        otpCode: Yup.string().required('Authentication code is required')
            .min(6, 'Code must be at least 6 characters')
            .max(10, 'Code cannot be more than 10 characters')
    })

    // HANDLE ADMIN LOGIN SUBMIT
    const HandleSubmit = async (values) => {
        setBackdrop(true);
        setUsername(values.username);
        setPassword(values.password);

        const loginData = {
            username: values.username,
            password: values.password
        };

        try {
            // Use the new TAS Admin login endpoint
            const response = await API.auth.adminLogin(loginData);

            if (response.status === 203 && response.data.mfaRequired) {
                setMfaRequired(true);
                setBackdrop(false);
                toast.info('Please enter your authentication code');
            } else if (response.status === 200 || response.status === 201) {
                handleSuccessfulLogin(response.data);
            } else {
                if (response.data.message) {
                    toast.error(response.data.message);
                } else {
                    toast.error('Invalid Credentials');
                }
                setBackdrop(false);
            }
        } catch (err) {
            setBackdrop(false);
            toast.error(err?.response?.data?.message || 'Invalid Credentials');
        }
    }

    // HANDLE MFA SUBMIT
    const HandleMfaSubmit = async (values) => {
        setBackdrop(true);

        // Create the complete MFA data object for admin
        const mfaData = {
            username: username,
            password: password,
            otpCode: values.otpCode
        };

        try {
            // Use new API system for MFA with admin login
            const response = await API.auth.adminLogin(mfaData);

            if (response.status === 200 || response.status === 201) {
                handleSuccessfulLogin(response.data);
            } else {
                if (response.data.message) {
                    toast.error(response.data.message);
                } else {
                    toast.error('Invalid Authentication Code');
                }
                setBackdrop(false);
            }
        } catch (err) {
            setBackdrop(false);
            toast.error(err?.response?.data?.message || 'Invalid Authentication Code');
        }
    }

    // HANDLE SUCCESSFUL ADMIN LOGIN
    const handleSuccessfulLogin = (responseData) => {
        localStorage.clear();
        
        // Build redirect URL with parameters just like regular login
        const secondAppUrl = process.env.REACT_APP_TAS_FE_LOCAL;

        const redirectUrl = `${secondAppUrl}?token=${responseData.access_token}&refresh_token=${responseData.refresh_token}&user_role=tasadmin&user_id=${responseData.userId}&keycloak_user_id=${responseData.userId}&user_type=tasadmin`;
        
        console.log('Admin redirecting to:', redirectUrl);
        window.location.href = redirectUrl;
    }

    // MFA FORM ELEMENT
    const renderMfaForm = () => {
        return (
            <Formik initialValues={mfaInitialValues} validationSchema={mfaValidationSchema} onSubmit={HandleMfaSubmit}>
                {() => (
                    <Form>
                        <Grow in timeout={1000}>
                            <Box>
                                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                                        Please enter the authentication code from your authenticator app
                                    </Typography>
                                </Box>

                                {/* OTP CODE INPUT */}
                                <div className={styles.inputContainer}>
                                    <InputField
                                        name="otpCode"
                                        type="text"
                                        placeholder="Authentication Code *"
                                        className={styles.inputField}
                                        maxLength={10}
                                        adornment={<VerifiedUserIcon />}
                                    />
                                </div>

                                <div>
                                    <Button
                                        sx={{ background: '#0A48B3', width: '100%', padding: '10px', mt: 2 }}
                                        type='submit'
                                        variant="contained"
                                    >
                                        Verify Code
                                    </Button>
                                </div>

                                <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: '#0A48B3', cursor: 'pointer', textDecoration: 'underline' }}
                                        onClick={() => setMfaRequired(false)}
                                    >
                                        Back to Login
                                    </Typography>
                                </Box>
                            </Box>
                        </Grow>
                    </Form>
                )}
            </Formik>
        );
    }

    const renderAdminLoginForm = () => {
        return (
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={HandleSubmit}>
                {(formikProps) => (
                    <Form>
                        <Grow in timeout={1000}>
                            <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 400 }}>
                                {/* TAS ADMIN HEADER */}
                                <Box sx={{ mb: 3, textAlign: 'center' }}>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0A48B3', mb: 1 }}>
                                        TAS Admin Portal
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Authorized Access Only
                                    </Typography>
                                </Box>

                                <TextField
                                    fullWidth
                                    label="Email"
                                    margin="normal"
                                    name="username"
                                    type="email"
                                    placeholder="Admin Email *"
                                    maxLength={50}
                                    value={formikProps.values.username}
                                    onChange={(e) => formikProps.setFieldValue('username', e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    label="Password"
                                    margin="normal"
                                    name="password"
                                    placeholder="Admin Password *"
                                    type={showPassword ? 'text' : 'password'}
                                    maxLength={50}
                                    value={formikProps.values.password}
                                    onChange={(e) => formikProps.setFieldValue('password', e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(prev => !prev)}>
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <Box display="flex" gap={2} mb={2} mt={3}>
                                    <CustomButton 
                                        size='custom' 
                                        padding="10px 20px" 
                                        fontSize="14px" 
                                        fullWidth 
                                        variant="outlined" 
                                        onClick={() => window.location.href = '/'}
                                    >
                                        BACK
                                    </CustomButton>
                                    <CustomButton 
                                        size='custom' 
                                        padding="10px 20px" 
                                        fontSize="14px" 
                                        fullWidth 
                                        variant="contained" 
                                        color="primary" 
                                        type='submit'
                                    >
                                        LOG IN
                                    </CustomButton>
                                </Box>

                                <Box textAlign="center" mt={2}>
                                    <Typography variant="caption" color="text.secondary">
                                        🔒 Secure Admin Authentication
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grow>
                    </Form>
                )}
            </Formik>
        );
    }

    return (
        <Container maxWidth='xs' sx={{ mt: 8 }}>
            {mfaRequired ? renderMfaForm() : renderAdminLoginForm()}

            {/* BACKDROP LOADER */}
            <Backdrop open={backdrop}>
                <CircularProgress style={{ color: 'white' }} />
            </Backdrop>
        </Container>
    );
}

export default AdminSignIn;